import React from 'react';
import ReactDOM from 'react-dom';
import './SplitPanel.css';
import { Flex } from 'app/components/common/Flex';

export interface SplitPanelProps {
    splitSize: string; //e.g. "20%", "200px"
    orientation: 'horizontal' | 'vertical';
    componentA: React.ReactNode;
    componentB: React.ReactNode;
    onResize?: () => void;
}

export interface SplitPanelState {
    dragging: boolean;
    splitSize: string; // will be in percentage once dragged
    dividerPosition: number;
}

const MINIMUM_SIZE_IN_PERCENTAGE = 10;
const MAXIMUM_SIZE_IN_PERCENTAGE = 90;

export default class SplitPanel extends React.PureComponent<SplitPanelProps, SplitPanelState> {
    private elementWithSizeRef = React.createRef<Flex>();
    private containerRef = React.createRef<HTMLDivElement>();

    constructor(props) {
        super(props);
        this.state = {
            dragging: false,
            splitSize: props.splitSize,
            dividerPosition: undefined,
        };
    }

    render() {
        return (
            <div ref={this.containerRef} className={['splitDivider ', this.props.orientation].join(' ')}>
                <Flex ref={this.elementWithSizeRef} size={this.state.splitSize}>
                    {this.props.componentA}
                </Flex>
                <div
                    className={['divider-section', this.props.orientation].join(' ')}
                    onPointerDown={this.handlePointerDown}
                    onPointerMove={this.handlePointerMove}
                    onPointerUp={this.handlePointerUp}>
                    <div className="divider" />
                </div>
                <Flex>{this.props.componentB}</Flex>
            </div>
        );
    }

    private getContainerSizeInPx(): number {
        if (this.containerRef?.current) {
            if ('horizontal' === this.props.orientation) {
                return this.containerRef.current.clientHeight;
            } else {
                return this.containerRef.current.clientWidth;
            }
        }
        return 0;
    }

    private getFlexComponentASizeInPx(): number {
        if (this.elementWithSizeRef?.current) {
            const element: Element = ReactDOM.findDOMNode(this.elementWithSizeRef.current) as Element;
            if ('horizontal' === this.props.orientation) {
                return element.clientHeight;
            } else {
                return element.clientWidth;
            }
        }
        return 0;
    }

    handlePointerDown = (e: React.PointerEvent) => {
        this.setState({
            dragging: true,
            dividerPosition: this.isHorizontalLayout() ? e.clientY : e.clientX,
        });
        e.currentTarget.setPointerCapture(e.pointerId);
    };

    handlePointerUp = (e: React.PointerEvent) => {
        if (this.state.dragging) {
            this.setState({
                dragging: false,
                dividerPosition: undefined,
            });
            e.currentTarget.releasePointerCapture(e.pointerId);
            if (this.props.onResize) {
                this.props.onResize();
            }
        }
    };

    handlePointerMove = (e: React.PointerEvent) => {
        if (this.state.dragging) {
            this.isHorizontalLayout() ? this.onMove(e.clientY) : this.onMove(e.clientX);
        }
    };

    onMove(clientValue: number) {
        //calculate the amount to move first in pixel as the clientValue is always in pixels
        const delta = this.state.dividerPosition - clientValue;

        //get the container size in Px, will depend of the orientation
        const containerSize = this.getContainerSizeInPx();

        //current componentA size (in %)
        const currentSize = (100 * this.getFlexComponentASizeInPx()) / containerSize;

        //convert delta to %
        const deltaInPercentage = (delta * 100) / containerSize;

        //apply to componentA the delta
        let newSizeInPercentage = currentSize - deltaInPercentage;

        //check the limits ([10%,90%])
        if (newSizeInPercentage > MAXIMUM_SIZE_IN_PERCENTAGE) {
            newSizeInPercentage = MAXIMUM_SIZE_IN_PERCENTAGE;
        }
        if (newSizeInPercentage < MINIMUM_SIZE_IN_PERCENTAGE) {
            newSizeInPercentage = MINIMUM_SIZE_IN_PERCENTAGE;
        }
        this.setState({
            splitSize: newSizeInPercentage + '%',
            dividerPosition: clientValue,
        });
    }

    private isHorizontalLayout() {
        return this.props.orientation === 'horizontal';
    }
}

export class HorizontalSplitPanelShowcase extends React.PureComponent {
    render() {
        return (
            <Flex direction={'vertical'}>
                <Flex size={'50%'}>
                    <SplitPanel splitSize="50%" orientation="horizontal" componentA={'TOP'} componentB={'BOTTOM'} />
                </Flex>
                <Flex>
                    <SplitPanel splitSize="50%" orientation="vertical" componentA={'LEFT'} componentB={'RIGHT'} />
                </Flex>
            </Flex>
        );
    }
}
